export const LOCAL_COOP_SHARED_CAMERA_VERSION = 1;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

function deepFreeze(value) {
  if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value;
  for (const child of Object.values(value)) deepFreeze(child);
  return Object.freeze(value);
}

function assertPlainObject(value, name) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new TypeError(`${name} must be an object`);
  }
  return value;
}

function assertPosition(value, name) {
  assertPlainObject(value, name);
  const position = {};
  for (const axis of ['x', 'y', 'z']) {
    if (!Number.isFinite(value[axis])) throw new RangeError(`${name}.${axis} must be finite`);
    position[axis] = value[axis];
  }
  return position;
}

function positionFromRecord(record, index) {
  assertPlainObject(record, `snakes[${index}]`);
  return assertPosition(record.snake?.position ?? record.position, `snakes[${index}].position`);
}

function pairwiseSeparation(positions) {
  let maximum = 0;
  for (let left = 0; left < positions.length; left += 1) {
    for (let right = left + 1; right < positions.length; right += 1) {
      const dx = positions[left].x - positions[right].x;
      const dy = positions[left].y - positions[right].y;
      const dz = positions[left].z - positions[right].z;
      maximum = Math.max(maximum, Math.hypot(dx, dy, dz));
    }
  }
  return maximum;
}

export function computeLocalCoopSharedCamera(snakes, options = {}) {
  if (!Array.isArray(snakes) || snakes.length === 0) {
    throw new RangeError('snakes must contain at least one player snake');
  }
  const minViewRadius = options.minViewRadius ?? 5;
  const maxViewRadius = options.maxViewRadius ?? 36;
  const padding = options.padding ?? 2.25;
  const softSeparation = options.softSeparation ?? 14;
  const hardSeparation = options.hardSeparation ?? 24;
  for (const [value, name] of [
    [minViewRadius, 'minViewRadius'],
    [maxViewRadius, 'maxViewRadius'],
    [padding, 'padding'],
    [softSeparation, 'softSeparation'],
    [hardSeparation, 'hardSeparation'],
  ]) {
    if (!Number.isFinite(value) || value <= 0) throw new RangeError(`${name} must be positive`);
  }
  if (maxViewRadius < minViewRadius) throw new RangeError('maxViewRadius must be at least minViewRadius');
  if (hardSeparation < softSeparation) throw new RangeError('hardSeparation must be at least softSeparation');

  const positions = snakes.map(positionFromRecord);
  const target = positions.reduce((sum, position) => ({
    x: sum.x + position.x,
    y: sum.y + position.y,
    z: sum.z + position.z,
  }), { x: 0, y: 0, z: 0 });
  target.x /= positions.length;
  target.y /= positions.length;
  target.z /= positions.length;

  let furthestFromTarget = 0;
  for (const position of positions) {
    furthestFromTarget = Math.max(
      furthestFromTarget,
      Math.hypot(position.x - target.x, position.y - target.y, position.z - target.z),
    );
  }
  const separation = pairwiseSeparation(positions);
  const requestedViewRadius = Math.max(minViewRadius, (furthestFromTarget * 1.9) + padding);
  const viewRadius = clamp(requestedViewRadius, minViewRadius, maxViewRadius);
  const separationState = separation >= hardSeparation
    ? 'hard-limit'
    : separation >= softSeparation ? 'warning' : 'together';

  return deepFreeze({
    version: LOCAL_COOP_SHARED_CAMERA_VERSION,
    playerCount: snakes.length,
    target,
    heading: 0,
    zoom: 1,
    viewRadius,
    requestedViewRadius,
    separation,
    separationState,
    softSeparation,
    hardSeparation,
    allPlayersFramed: requestedViewRadius <= maxViewRadius,
  });
}
